import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import type { KeyResult, KrStatus, KrType, Quarter } from "@/types/entities";
import { toast } from "sonner";

export interface KeyResultFilters {
  objective_id?: string;
  owner_id?: string;
  quarter?: Quarter;
  status?: KrStatus;
  type?: KrType;
  cursor?: string;
  limit?: number;
}

export function useKeyResults(filters: KeyResultFilters = {}) {
  return useQuery({
    queryKey: ["key-results", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/key-results", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useKeyResult(id: string) {
  return useQuery({
    queryKey: ["key-results", id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/key-results/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateKeyResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateKeyResultRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/key-results", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
      toast.success("Key Result created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateKeyResult(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateKeyResultRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/key-results/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["key-results", id] });
      const previous = queryClient.getQueryData<{ data: KeyResult }>(["key-results", id]);
      if (previous) {
        queryClient.setQueryData(["key-results", id], {
          ...previous,
          data: { ...previous.data, ...newData },
        });
      }
      return { previous };
    },
    onError: (_error: Error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["key-results", id], context.previous);
      }
      toast.error("Failed to save changes");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
    },
  });
}

export function useDeleteKeyResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/key-results/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
      toast.success("Key Result deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useMoveKeyResult(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (objective_id: string) => {
      const { data, error } = await apiClient.PATCH("/v1/key-results/{id}/move", {
        params: { path: { id } },
        body: { objective_id },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
      void queryClient.invalidateQueries({ queryKey: ["objectives"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateKeyResultStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (status: KrStatus) => {
      const { data, error } = await apiClient.PATCH("/v1/key-results/{id}/status", {
        params: { path: { id } },
        body: { status },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export interface UseCheckInKeyResultOptions {
  silent?: boolean;
}

interface CheckInSnapshot {
  detail?: { data: KeyResult };
  lists: Array<[readonly unknown[], unknown]>;
}

function applyCheckInToKr(kr: KeyResult, body: components["schemas"]["CheckInRequest"]): KeyResult {
  const next: KeyResult = { ...kr };
  if (body.completion_percent !== undefined) {
    next.completion_percent = body.completion_percent;
  }
  if (body.current_value !== undefined) {
    next.current_value = body.current_value;
  }
  return next;
}

export function useCheckInKeyResult(
  id: string,
  options: UseCheckInKeyResultOptions = {},
) {
  const queryClient = useQueryClient();
  const { silent = false } = options;
  return useMutation({
    mutationFn: async (body: components["schemas"]["CheckInRequest"]) => {
      const { data, error } = await apiClient.PATCH("/v1/key-results/{id}/check-in", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onMutate: async (body): Promise<CheckInSnapshot> => {
      await queryClient.cancelQueries({ queryKey: ["key-results"] });

      const detail = queryClient.getQueryData<{ data: KeyResult }>(["key-results", id]);
      const lists = queryClient.getQueriesData({ queryKey: ["key-results"] });
      const snapshot: CheckInSnapshot = { detail, lists };

      if (detail) {
        queryClient.setQueryData<{ data: KeyResult }>(["key-results", id], {
          ...detail,
          data: applyCheckInToKr(detail.data, body),
        });
      }

      for (const [key, value] of lists) {
        if (key.length === 2 && key[1] === id) continue;
        if (
          value &&
          typeof value === "object" &&
          "data" in (value as object) &&
          Array.isArray((value as { data: unknown }).data)
        ) {
          const list = value as { data: KeyResult[] } & Record<string, unknown>;
          let mutated = false;
          const nextData = list.data.map((kr) => {
            if (kr.id === id) {
              mutated = true;
              return applyCheckInToKr(kr, body);
            }
            return kr;
          });
          if (mutated) {
            queryClient.setQueryData(key, { ...list, data: nextData });
          }
        }
      }

      return snapshot;
    },
    onError: (error: Error, _body, context) => {
      if (context?.detail) {
        queryClient.setQueryData(["key-results", id], context.detail);
      }
      if (context?.lists) {
        for (const [key, value] of context.lists) {
          queryClient.setQueryData(key, value);
        }
      }
      toast.error(error.message);
    },
    onSuccess: () => {
      if (!silent) {
        toast.success("Check-in recorded");
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["key-results"] });
    },
  });
}
